import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-950 px-6 py-16">
      <div className="max-w-3xl mx-auto space-y-8">
        <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
        <p className="text-slate-400 text-sm">Last updated: April 10, 2025</p>

        <Section title="1. Introduction">
          Cipher ("we", "our", "us") operates the cipher-buyers.com platform — a performance marketing
          operating system. This Privacy Policy explains how we collect, use, and protect information
          when you use our services.
        </Section>

        <Section title="2. Information We Collect">
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong>Account Information:</strong> Name, email address, and role when you create an account or sign in via Google OAuth.</li>
            <li><strong>Meta Ads Data:</strong> When you connect a Meta (Facebook) ad account, we access ad campaign performance data, creative engagement metrics, and ad spend information via the Meta Marketing API using the <code className="text-emerald-400">ads_read</code> and <code className="text-emerald-400">ads_management</code> permissions you authorize.</li>
            <li><strong>Usage Data:</strong> Interaction logs within the platform (pages visited, features used) to improve the product experience.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>To provide and operate the Cipher platform, including offer scoring, creative engine, campaign health monitoring, and competitor intelligence.</li>
            <li>To retrieve and display your Meta ad account data within the platform for analytics, dual attribution, and creative performance tracking.</li>
            <li>To generate recommendations and velocity metrics based on your campaign data.</li>
            <li>We do <strong>not</strong> sell, rent, or share your personal data or ad account data with third parties.</li>
          </ul>
        </Section>

        <Section title="4. Meta Platform Data">
          We access Meta platform data solely to provide you with advertising analytics and management
          features within Cipher. Specifically:
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>Ad performance metrics (impressions, clicks, spend, conversions) are used for campaign health dashboards and P&amp;L reporting.</li>
            <li>Creative engagement data is used for the Creative Engine pipeline and dual attribution analysis.</li>
            <li>We store encrypted OAuth tokens and ad account identifiers. We do not store raw ad creative content.</li>
            <li>You can disconnect your Meta account at any time from Settings, which revokes our access and deletes stored tokens.</li>
          </ul>
        </Section>

        <Section title="5. Data Storage & Security">
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>All data is stored in encrypted databases with access restricted to authorized personnel.</li>
            <li>Meta OAuth tokens are encrypted at rest using Fernet symmetric encryption.</li>
            <li>We use HTTPS for all data transmission.</li>
            <li>We retain your data only as long as your account is active. Upon account deletion, all associated data is permanently removed within 30 days.</li>
          </ul>
        </Section>

        <Section title="6. Third-Party Services">
          Cipher integrates with the following third-party services:
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong>Meta (Facebook) Marketing API</strong> — for ad account data retrieval and management.</li>
            <li><strong>Google OAuth</strong> — for authentication.</li>
            <li><strong>Google Drive API</strong> — for creative asset and script storage (when authorized).</li>
          </ul>
          Each service is governed by its own privacy policy. We only request the minimum permissions necessary.
        </Section>

        <Section title="7. Your Rights">
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong>Access:</strong> You can request a copy of all data we hold about you.</li>
            <li><strong>Deletion:</strong> You can request deletion of your account and all associated data.</li>
            <li><strong>Revoke Access:</strong> You can disconnect third-party integrations (Meta, Google) at any time from Settings.</li>
            <li><strong>Opt Out:</strong> You can stop using the platform at any time.</li>
          </ul>
        </Section>

        <Section title="8. Data Deletion Instructions">
          To request deletion of your data:
          <ol className="list-decimal pl-5 space-y-2 mt-2">
            <li>Go to Settings within the Cipher platform.</li>
            <li>Click "Disconnect" on any connected accounts (Meta, Google).</li>
            <li>Contact us at the email below to request full account deletion.</li>
          </ol>
          We will process deletion requests within 30 days.
        </Section>

        <Section title="9. Contact Us">
          If you have questions about this Privacy Policy or your data, contact us at:{' '}
          <a href="mailto:privacy@cipher-buyers.com" className="text-emerald-400 hover:underline">
            privacy@cipher-buyers.com
          </a>
        </Section>

        <div className="border-t border-slate-800 pt-6 text-slate-500 text-sm">
          &copy; {new Date().getFullYear()} Cipher. All rights reserved.
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <h2 className="text-lg font-semibold text-white">{title}</h2>
    <div className="text-slate-300 text-sm leading-relaxed">{children}</div>
  </div>
);

export default PrivacyPolicy;
